import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { Navigate, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MyResultsTab } from "./FinanceCommissionsPanel";
import {
  FinanceCompetenciaPicker,
  FinancePanelIntro,
  parseCompetencia,
} from "./FinanceUi";
import { FINANCE_GLOSS, FINANCE_LABEL } from "./FinanceCopy";
import { usePlatformConsoleAcl } from "./PlatformConsoleAclContext";
import { PLATFORM_CONSOLE_PATH } from "./platformConsoleRoutes";

function currentYearMonthLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function useConsoleOutlet() {
  const ctx = useOutletContext() || {};
  return {
    registerRefresh: typeof ctx.registerRefresh === "function" ? ctx.registerRefresh : () => {},
    stopRefreshing: typeof ctx.stopRefreshing === "function" ? ctx.stopRefreshing : () => {},
  };
}

/**
 * Página de menu: resultados pessoais de comissão/bônus.
 * Só acessível se o usuário estiver cadastrado em Incentivos → Pessoas.
 */
export default function MyIncentivesPage() {
  const { t } = useTranslation("app");
  const { loading, financePerson } = usePlatformConsoleAcl();
  const { registerRefresh, stopRefreshing } = useConsoleOutlet();
  const [competencia, setCompetencia] = useState(currentYearMonthLocal);
  const [panelKey, setPanelKey] = useState(0);

  const yearMonth = useMemo(() => {
    const parsed = parseCompetencia(competencia);
    if (parsed.scope === "year") {
      return `${parsed.year}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;
    }
    return parsed.value || currentYearMonthLocal();
  }, [competencia]);

  useEffect(() => {
    registerRefresh(() => setPanelKey((k) => k + 1));
    return () => registerRefresh(() => stopRefreshing());
  }, [registerRefresh, stopRefreshing]);

  if (loading) return null;
  if (!financePerson) {
    return <Navigate to={PLATFORM_CONSOLE_PATH.workspaces} replace />;
  }

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="h6" fontWeight={700}>
          {t("platformPage.navMyIncentives", { defaultValue: FINANCE_LABEL.myResults })}
        </Typography>
        <FinanceCompetenciaPicker value={competencia} onChange={setCompetencia} />
      </Stack>

      <FinancePanelIntro
        title={financePerson.name || FINANCE_LABEL.myResults}
        description={FINANCE_GLOSS.myResultsIntro}
      />

      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        {t("platformPage.myIncentivesMenuHint", {
          defaultValue:
            "Seus resultados de comissão do time nesta competência. Parcelas futuras dependem de você continuar no ciclo e do cliente permanecer ativo.",
        })}
      </Alert>

      <MyResultsTab key={panelKey} yearMonth={yearMonth} />
    </Box>
  );
}
